import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DriverScheduleEditor, { emptySchedule } from "@/components/DriverScheduleEditor";

const PCT_PRESETS = [50, 60, 65, 70, 80, 90];

export default function UserEditDialog({ open, onOpenChange, user, onSaved }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "operator", driver_pct: 70, active: true, password: "", schedule: emptySchedule(), card_access: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: user.role,
        driver_pct: user.driver_pct ?? 70,
        active: user.active,
        password: "",
        schedule: user.schedule || emptySchedule(),
        card_access: !!user.card_access,
      });
    }
  }, [user, open]);

  const submit = async () => {
    if (!user) return;
    setBusy(true);
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: form.role,
      active: form.active,
    };
    if (form.role === "operator") {
      payload.driver_pct = Number(form.driver_pct);
      payload.schedule = form.schedule;
    }
    if (form.role === "dispatcher") payload.card_access = !!form.card_access;
    if (form.password) payload.password = form.password;
    try {
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Usuario actualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (!user) return null;
  const isOperator = form.role === "operator";
  const llcPct = 100 - Number(form.driver_pct || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[92vh] overflow-y-auto" data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Editar usuario</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs font-mono text-muted-foreground tracking-widest">
            ID: {user.id}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input data-testid="edit-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Teléfono</Label>
              <Input data-testid="edit-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              data-testid="edit-user-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-user-role" className="bg-input border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher</SelectItem>
                  <SelectItem value="operator">Chofer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Estado</Label>
              <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                <SelectTrigger data-testid="edit-user-active" className="bg-input border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Activo</SelectItem>
                  <SelectItem value="0">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isOperator && (
            <div className="pt-3 border-t border-border space-y-2">
              <div className="flex items-center justify-between">
                <Label>Comisión del chofer</Label>
                <div className="font-mono text-sm">
                  <span className="text-emerald-400 font-bold">{form.driver_pct}%</span>
                  <span className="text-muted-foreground"> / </span>
                  <span className="text-cyan-400 font-bold">{llcPct}%</span>
                  <span className="text-muted-foreground text-xs"> (LLC)</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {PCT_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm({ ...form, driver_pct: p })}
                    data-testid={`pct-preset-${p}`}
                    className={`text-xs font-mono font-bold px-3 py-1.5 rounded-full border transition-colors ${
                      Number(form.driver_pct) === p
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-white"
                    }`}
                  >
                    {p}/{100 - p}
                  </button>
                ))}
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  data-testid="edit-user-driver-pct"
                  value={form.driver_pct}
                  onChange={(e) => setForm({ ...form, driver_pct: e.target.value })}
                  className="w-24 h-8 bg-input border-border font-mono"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Aplica al crear nuevos viajes: <span className="font-mono">driver_income = precio × {form.driver_pct}%</span>
              </div>
            </div>
          )}

          {isOperator && (
            <div className="pt-3 border-t border-border">
              <DriverScheduleEditor value={form.schedule} onChange={(s) => setForm({ ...form, schedule: s })} />
            </div>
          )}

          {form.role === "dispatcher" && (
            <label htmlFor="user-card-access" className="flex items-start gap-3 rounded-md border border-border bg-input/40 px-3 py-2.5 cursor-pointer" data-testid="user-card-access-row">
              <input
                id="user-card-access" type="checkbox" checked={!!form.card_access}
                onChange={(e) => setForm({ ...form, card_access: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="user-card-access"
              />
              <span>
                <span className="block text-sm font-bold text-white">Acceso a tarjetas</span>
                <span className="block text-xs text-muted-foreground">Puede ver (revelar) el método de pago guardado de los clientes. Cada vista queda registrada. Sin esto, el botón no aparece.</span>
              </span>
            </label>
          )}

          <div className="pt-3 border-t border-border space-y-1">
            <Label>Nueva contraseña <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input data-testid="edit-user-password" type="password" placeholder="Dejar vacío para mantener" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.name}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
            data-testid="edit-user-submit"
          >
            {busy ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
